import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import filterNulls from "@/lib/filterNulls"
import {
  CreateAndApplySiteLabelMutation,
  LabelColor,
} from "@/v2/graphql/public-generated-rq"
import { useOrgSlugBridge } from "@/v2/hooks/useOrgSlugBridge"
import { Button, Popover, Spinner, twMerge } from "@lumen-energy/design-system"
import { IconPlus } from "@tabler/icons-react"

import { AddLabelPopoverContent } from "./AddLabelPopoverContent"
import { EditSiteLabelPopoverContent } from "./EditSiteLabelPopoverContent"
import { ExistingLabelsList } from "./ExistingLabelsList"
import { Label, LabelAdd, LabelProps } from "./Label"
import useOrgLabelsOps from "./useOrgLabelsOps"
import useSiteLabelsOps from "./useSiteLabelsOps"

export enum LabelThemeColors {
  ACCENT_1 = "accent-1",
  ACCENT_2 = "accent-2",
  ACCENT_3 = "accent-3",
  BRAND_PRIMARY = "primary",
  BRAND_SECONDARY = "secondary",
  ERROR = "error",
  NEUTRAL = "neutral",
  SUCCESS = "success",
}
type LabelThemeColorsKeys = keyof typeof LabelThemeColors

export interface LabelGroupProps {
  applyLabelToSite: (siteLabelPid: string, labelName: string) => void
  createLabelAndApplyToSite: (name: string, themeColor: LabelColor) => void
  deleteLabel: (siteLabelPid: string) => void
  editLabelColor: (labelPid: string, color: LabelColor) => void
  editLabelName: (labelPid: string, name: string) => void
  labels: Array<LabelProps>
  maxVisibleLabels?: number
  orgLabels: Array<LabelProps>
  removeLabel: (labelPid: string, labelName: string) => void
  className?: string
}

export interface TempLabelsProps {
  setTempLabels: Dispatch<SetStateAction<LabelProps[]>>
  tempLabels: Array<LabelProps>
}

export const retrieveColorKey = (labelColor: LabelThemeColors) => {
  // TODO getting enum key from value here; there is probably a cleaner way to do this?
  return Object.keys(LabelThemeColors)[
    Object.values(LabelThemeColors).indexOf(
      labelColor as (typeof LabelThemeColors)[LabelThemeColorsKeys],
    )
  ]
}

export function getLabelColorKeyFromValue(value: string) {
  return Object.entries(LabelColor).find(([_, val]) => val === value)?.[0]
}

export function LabelGroup({
  applyLabelToSite,
  createLabelAndApplyToSite,
  deleteLabel,
  editLabelColor,
  editLabelName,
  labels = [],
  orgLabels,
  maxVisibleLabels = 3,
  removeLabel,
  className,
}: LabelGroupProps) {
  const popoverAlignment = "start"
  const popoverSide = "bottom"
  const popoverSideOffset = 5
  const popoverSharedClasses =
    "max-w-[250px] p-0 rounded border-[0.016rem] border-colors-border-secondary"

  const hideExtraLabels = labels.length > maxVisibleLabels
  const extraLabelsCount = labels.length - maxVisibleLabels

  // tempLabels are part of a batch update that hasn't been dispatched yet
  const [tempLabels, setTempLabels] = useState<Array<LabelProps>>([])

  useEffect(() => {
    setTempLabels(labels)
  }, [labels])

  const handleSendBatchedUpdate = useCallback(async () => {
    // If labels to add or remove have been batched while the popover is open, send them after popover closes
    // TODO: Sending as individual add / apply / remove requests for now; will need a way to replace/update labels for a site

    // Filter out the labels that only exist in tempLabels as newLabels
    // New labels do not yet have a label pid assigned
    const newLabels = tempLabels.filter(
      (newLabel) => !orgLabels.some((orgLabel) => newLabel.name === orgLabel.name),
    )
    if (newLabels?.length > 0) {
      newLabels.forEach(
        async (label: LabelProps) =>
          await createLabelAndApplyToSite(label.name, label.themeColor),
      )
    }

    // Filter out newly applied labels that already existed for a given org
    const applyLabels = tempLabels
      .filter((tempLabel) => !labels.some((label) => tempLabel.pid === label.pid))
      .filter((tempLabel) =>
        orgLabels.some((orgLabel) => tempLabel.pid === orgLabel.pid),
      )
    if (applyLabels?.length > 0) {
      applyLabels.forEach(
        async (label: LabelProps) => await applyLabelToSite(label.pid, label.name),
      )
    }

    // Filter out labels that originally existed on labels, but are not in tempLabels
    const removeLabels = labels.filter(
      (label) => !tempLabels.find((tempLabel) => label.name === tempLabel.name),
    )
    if (removeLabels?.length > 0) {
      removeLabels.forEach(
        async (label: LabelProps) => await removeLabel(label.pid, label.name),
      )
    }
  }, [
    applyLabelToSite,
    createLabelAndApplyToSite,
    labels,
    orgLabels,
    removeLabel,
    tempLabels,
  ])

  return (
    <div className={twMerge("flex gap-2", className)}>
      {labels.length > 0 &&
        labels.map(
          (label, index) =>
            // If a site has more than maxVisibleLabels count, hide remaining labels within a "...more" button
            index < maxVisibleLabels && (
              <Popover.Root key={`label-${index}`}>
                <Popover
                  align={popoverAlignment}
                  side={popoverSide}
                  sideOffset={popoverSideOffset}
                  content={
                    <EditSiteLabelPopoverContent
                      editLabelColor={editLabelColor}
                      editLabelName={editLabelName}
                      label={label}
                      orgLabels={orgLabels}
                      removeLabel={removeLabel}
                    />
                  }
                  className={twMerge(popoverSharedClasses, "p-4")}
                  onOpenAutoFocus={(event) => event.preventDefault()}
                >
                  <Label {...label} />
                </Popover>
              </Popover.Root>
            ),
        )}

      {/* Add new label Popover */}
      {!hideExtraLabels && (
        <Popover.Root>
          <Popover
            align={popoverAlignment}
            side={popoverSide}
            sideOffset={popoverSideOffset}
            content={
              <AddLabelPopoverContent
                orgLabels={orgLabels}
                tempLabels={tempLabels}
                setTempLabels={setTempLabels}
                handleSendBatchedUpdate={handleSendBatchedUpdate}
                removeLabel={removeLabel}
                editLabelColor={editLabelColor}
                editLabelName={editLabelName}
                deleteLabel={deleteLabel}
              />
            }
            className={popoverSharedClasses}
            onEscapeKeyDown={() => setTempLabels(labels)}
            onInteractOutside={() => setTempLabels(labels)}
          >
            <LabelAdd />
          </Popover>
        </Popover.Root>
      )}

      {/* "more..." button appears if number of labels exceeds maxVisibleLabels */}
      {hideExtraLabels && (
        <Popover.Root>
          <Popover
            align={popoverAlignment}
            side={popoverSide}
            sideOffset={popoverSideOffset}
            content={
              <>
                <ExistingLabelsList
                  orgLabels={orgLabels}
                  tempLabels={tempLabels}
                  setTempLabels={setTempLabels}
                  removeLabel={removeLabel}
                  editLabelColor={editLabelColor}
                  editLabelName={editLabelName}
                  deleteLabel={deleteLabel}
                  labelsToShow="site"
                  className="p-4"
                />
                <div className="pb-2">
                  <hr className="mt-2 mb-2 border-colors-border-secondary" />
                  <Popover.Root>
                    <Popover
                      align="start"
                      side="top"
                      content={
                        <AddLabelPopoverContent
                          orgLabels={orgLabels}
                          tempLabels={tempLabels}
                          setTempLabels={setTempLabels}
                          handleSendBatchedUpdate={handleSendBatchedUpdate}
                          removeLabel={removeLabel}
                          editLabelColor={editLabelColor}
                          editLabelName={editLabelName}
                          deleteLabel={deleteLabel}
                        />
                      }
                      className={popoverSharedClasses}
                      onEscapeKeyDown={() => setTempLabels(labels)}
                      onInteractOutside={() => setTempLabels(labels)}
                    >
                      <LabelAdd className="ml-2" />
                    </Popover>
                  </Popover.Root>
                </div>
              </>
            }
            className={twMerge("w-[250px]", popoverSharedClasses)}
          >
            <Button
              size="xsmall"
              variant="neutral"
              className="whitespace-pre bg-brand-primary-100 hover:bg-brand-primary-200 aria-expanded:bg-brand-primary-200"
              noBorder
            >
              +{extraLabelsCount} more&#x2026;
            </Button>
          </Popover>
        </Popover.Root>
      )}
    </div>
  )
}

// ------------
// Bulk Actions:

export interface LabelGroupBulkActionProps {
  applyLabelToSites: (
    siteLabelPid: string,
    sitePids: Array<string>,
    labelName: string,
  ) => void
  createLabelAndApplyToSite: (
    sitePid: string,
    name: string,
    themeColor: LabelColor,
    callback?: (result: CreateAndApplySiteLabelMutation) => void,
  ) => void
  editLabelColor: (labelPid: string, color: LabelColor) => void
  editLabelName: (labelPid: string, name: string) => void
  orgLabels: Array<LabelProps>
  removeLabelFromSites: (
    siteLabelPid: string,
    sitePids: Array<string>,
    labelName: string,
  ) => void
  sitePids: Array<string>
  onExit: () => void
}

export function LabelGroupBulkAction({
  applyLabelToSites,
  createLabelAndApplyToSite,
  editLabelColor,
  editLabelName,
  orgLabels = [],
  removeLabelFromSites,
  sitePids,
  onExit,
}: LabelGroupBulkActionProps) {
  const popoverAlignment = "start"
  const popoverSide = "bottom"
  const popoverSideOffset = 5
  const popoverSharedClasses = "max-w-[250px] p-0"

  // tempLabels are part of a batch update that hasn't been dispatched yet
  const [tempLabels, setTempLabels] = useState<Array<LabelProps>>([])
  const buttonRef = useRef<HTMLButtonElement>(null)

  const handleSendBatchedUpdate = useCallback(() => {
    // If labels to add or remove have been batched while the popover is open, send them after popover closes

    // New labels creation
    const newLabels = tempLabels.filter(
      (newLabel) => !orgLabels.some((orgLabel) => newLabel.name === orgLabel.name),
    )
    // Label must be created and applied on the first sitePid, then applied to all other pids
    const newLabelCreationCallback = (result: CreateAndApplySiteLabelMutation) => {
      if (sitePids.length > 1) {
        // The first sitePid has already had the label applied upon creation
        const remainingSitePids = [...sitePids]
        remainingSitePids.shift()

        // The result of the CreateAndApplySiteLabelMutation has the newly-created label pid
        if (
          result?.createAndApplySiteLabel?.__typename == "CreateAndApplySiteLabelOutput"
        ) {
          const resultLabel = result?.createAndApplySiteLabel?.label
          applyLabelToSites(resultLabel.pid, remainingSitePids, resultLabel.name)
        }
      }
    }
    if (newLabels?.length > 0) {
      newLabels.forEach(async (label: LabelProps) => {
        await createLabelAndApplyToSite(
          sitePids[0],
          label.name,
          label.themeColor,
          newLabelCreationCallback,
        )
      })
    }

    // Apply existing labels
    const existingLabels = tempLabels.filter(
      (existingLabel) =>
        !newLabels.some((newLabel) => existingLabel.name === newLabel.name),
    )
    if (existingLabels?.length > 0) {
      existingLabels.forEach(
        async (label: LabelProps) =>
          await applyLabelToSites(label.pid, sitePids, label.name),
      )
    }

    // Exit
    // Close Popover
    buttonRef?.current?.click()

    // Reset tempLabels
    setTempLabels([])

    // Call onExit (typically will reset selected rows)
    onExit()
  }, [
    applyLabelToSites,
    createLabelAndApplyToSite,
    onExit,
    orgLabels,
    sitePids,
    tempLabels,
  ])

  const handleRemoveLabels = (labelPid: string, labelName: string) =>
    removeLabelFromSites(labelPid, sitePids, labelName)

  return (
    // For bulk actions, labels don't render in a row, but rather an "Add label" button displays
    <Popover.Root>
      <Popover
        align={popoverAlignment}
        side={popoverSide}
        sideOffset={popoverSideOffset}
        content={
          <>
            <AddLabelPopoverContent
              helperText="Label will only apply to selected properties"
              orgLabels={orgLabels}
              tempLabels={tempLabels}
              setTempLabels={setTempLabels}
              handleSendBatchedUpdate={handleSendBatchedUpdate}
              removeLabel={handleRemoveLabels}
              editLabelColor={editLabelColor}
              editLabelName={editLabelName}
              isBulkAction
            />
          </>
        }
        className={popoverSharedClasses}
        onEscapeKeyDown={() => setTempLabels([])}
        onInteractOutside={() => setTempLabels([])}
      >
        <Button
          size="medium"
          variant="tertiary"
          iconPosition="left"
          icon={IconPlus}
          ref={buttonRef}
          tabIndex={0}
        >
          Add label
        </Button>
      </Popover>
    </Popover.Root>
  )
}

export const SimpleLabelGroup = ({
  propertyPid,
  orgSlug,
  maxVisibleLabels = 3,
  readOnly = false,
  className,
}: {
  propertyPid: string
  orgSlug?: string
  maxVisibleLabels?: number
  readOnly?: boolean
  className?: string
}) => {
  const maybeOrgSlug = useOrgSlugBridge()

  const {
    handleDeleteLabel,
    handleEditLabelColor,
    handleEditLabelName,
    orgLabels,
    orgSiteLabelsIsLoading,
  } = useOrgLabelsOps({
    refetchSites: () => {},
    orgSlug: orgSlug || maybeOrgSlug,
  })

  const { handleCreateAndApplySiteLabel, handleRemoveLabel, handleApplyLabelToSite } =
    useSiteLabelsOps({
      orgSlug: orgSlug || maybeOrgSlug,
    })

  const siteLabels = useMemo(() => {
    return filterNulls(
      orgLabels.map((label) => {
        if (!label.sites.includes(propertyPid)) return null
        return {
          pid: label.pid,
          name: label.name,
          themeColor: label.themeColor,
        }
      }),
    )
  }, [orgLabels, propertyPid])

  if (orgSiteLabelsIsLoading) return <Spinner />

  if (readOnly) {
    if (siteLabels.length === 0) return null
    return (
      <div className={twMerge("flex flex-wrap gap-2", className)}>
        {siteLabels.map((label) => (
          <Label key={label.pid} {...label} />
        ))}
      </div>
    )
  }

  return (
    <LabelGroup
      className={className}
      createLabelAndApplyToSite={(labelName: string, labelColor: LabelColor) => {
        handleCreateAndApplySiteLabel({
          sitePid: propertyPid,
          labelName,
          labelColor,
        })
      }}
      applyLabelToSite={(siteLabelPid: string, labelName: string) =>
        handleApplyLabelToSite({
          siteLabelPid,
          sitePid: propertyPid,
          labelName,
        })
      }
      editLabelColor={handleEditLabelColor}
      editLabelName={handleEditLabelName}
      labels={siteLabels}
      orgLabels={orgLabels}
      removeLabel={(siteLabelPid: string, labelName: string) =>
        handleRemoveLabel({ siteLabelPid, sitePid: propertyPid, labelName })
      }
      deleteLabel={handleDeleteLabel}
      maxVisibleLabels={maxVisibleLabels}
    />
  )
}
